import {
  LoanQuoteRequirement,
  LoanQuoteRequirementPhase,
  LoanQuoteRequirementStatus,
} from '@axo/shared/data-access/types/loan_quote';
import { Divider, Icon, Tooltip } from '@blueprintjs/core';
import styles from './LoanQuoteRequirements.module.css';
import { classNames } from '@axo/shared/util/dom';
import { formatDatetime } from '../../../../utils/formatDate.ts';

export interface LoanQuoteRequirementsProps {
  data: LoanQuoteRequirement[];
}

function RequirementStatusDisplay({
  status,
}: {
  status: LoanQuoteRequirementStatus;
}) {
  const icon = resolveIconByStatus();

  function resolveIconByStatus() {
    switch (status) {
      case LoanQuoteRequirementStatus.RESOLVED:
        return 'tick';
      case LoanQuoteRequirementStatus.SENT:
      case LoanQuoteRequirementStatus.RECEIVED:
        return 'time';
      default:
        return 'warning-sign';
    }
  }

  function getStatusLabel(status: LoanQuoteRequirementStatus) {
    switch (status) {
      case LoanQuoteRequirementStatus.RESOLVED:
        return 'Resolved';
      case LoanQuoteRequirementStatus.SENT:
        return 'Sent';
      case LoanQuoteRequirementStatus.RECEIVED:
        return 'Received';
      default:
        return 'Pending';
    }
  }
  return (
    <div className={classNames(styles.status, styles[status]).trim()}>
      <strong>
        {getStatusLabel(status)} <Icon icon={icon} />
      </strong>
    </div>
  );
}

export const LoanQuoteRequirements = (
  loanQuoteRequirements: LoanQuoteRequirementsProps
) => {
  const requirementsPerPhase =
    loanQuoteRequirements.data?.reduce<
      Record<string | LoanQuoteRequirementPhase, LoanQuoteRequirement[]>
    >((acc, item) => {
      if (!acc[item.Phase]) {
        acc[item.Phase] = [];
      }
      acc[item.Phase].push(item);
      return acc;
    }, {}) ?? {};

  function getKindLabel(kind: string) {
    if (!kind) return;
    return kind
      .toLowerCase()
      .split('_')
      .map((word, index) =>
        index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
      )
      .join(' ');
  }

  function getPhaseLabel(phase: LoanQuoteRequirementPhase) {
    switch (phase) {
      case LoanQuoteRequirementPhase.BEFORE_ACCEPTANCE:
        return 'acceptance';
      case LoanQuoteRequirementPhase.BEFORE_APPROVAL:
        return 'approval';
      case LoanQuoteRequirementPhase.BEFORE_DISBURSEMENT:
        return 'disbursement';
      default:
        return '';
    }
  }

  function resolveStatusByDate(requirement: LoanQuoteRequirement) {
    if (requirement.ResolvedAt) {
      return LoanQuoteRequirementStatus.RESOLVED;
    } else if (requirement.ReceivedAt) {
      return LoanQuoteRequirementStatus.RECEIVED;
    } else if (requirement.SentAt) {
      return LoanQuoteRequirementStatus.SENT;
    } else {
      return LoanQuoteRequirementStatus.PENDING;
    }
  }

  return Object.keys(requirementsPerPhase).map((requirementPhase) => {
    const applicantRequirements = requirementsPerPhase[requirementPhase].filter(
      (item: LoanQuoteRequirement) => item.ApplicantNumber === 1
    );
    const coApplicantRequirements = requirementsPerPhase[
      requirementPhase
    ].filter((item: LoanQuoteRequirement) => item.ApplicantNumber === 2);
    return (
      <div key={requirementPhase} className={styles.wrapper}>
        <div className={styles.title}>
          Documentation for{' '}
          {getPhaseLabel(requirementPhase as LoanQuoteRequirementPhase)}
        </div>
        <div className={styles.container}>
          {applicantRequirements.length !== 0 && (
            <div className={styles.requirementCard}>
              <div className={styles.requirementCardHeader}>
                <Icon icon="person" />
                <span>Applicant</span>
              </div>
              <Divider />
              <div className={styles.requirementCardBody}>
                {applicantRequirements.map((requirement) => (
                  <div className={styles.requirementItem} key={requirement.ID}>
                    <div className={styles.requirementItemKind}>
                      {getKindLabel(requirement.Kind)}
                    </div>
                    <div className={styles.requirementItemStatus}>
                      <Tooltip
                        disabled={!requirement.SentAt}
                        placement={'top'}
                        content={`Sent at: ${formatDatetime(
                          requirement.SentAt
                        )}`}
                        openOnTargetFocus={false}
                      >
                        <RequirementStatusDisplay
                          status={resolveStatusByDate(requirement)}
                        />
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {coApplicantRequirements.length !== 0 && (
            <div className={styles.requirementCard}>
              <div className={styles.requirementCardHeader}>
                <Icon icon="people" />
                <span>Co applicant</span>
              </div>
              <Divider />
              <div className={styles.requirementCardBody}>
                {coApplicantRequirements.map((requirement) => (
                  <div className={styles.requirementItem} key={requirement.ID}>
                    <div className={styles.requirementItemKind}>
                      {getKindLabel(requirement.Kind)}
                    </div>
                    <div className={styles.requirementItemStatus}>
                      <Tooltip
                        disabled={!requirement.SentAt}
                        placement={'top'}
                        content={`Sent at: ${formatDatetime(
                          requirement.SentAt
                        )}`}
                        openOnTargetFocus={false}
                      >
                        <RequirementStatusDisplay
                          status={resolveStatusByDate(requirement)}
                        />
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  });
};
